'use client';

import { ReactNode, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useChainId, WagmiProvider } from 'wagmi';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { hashFn } from '@wagmi/core/query';
import { OnchainKitProvider } from '@coinbase/onchainkit';
import { createWagmiConfig } from '@/onchain/store/createWagmiConfig';
import {
  DEFAULT_WAGMI_QUERY_CACHE_TIME,
  DEFAULT_WAGMI_QUERY_STALE_TIME,
} from '@/onchain/querySettings';
import { getChainById } from '@/onchain/store/supportedChains';

type Props = { children: ReactNode };

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryKeyHashFn: hashFn,
      staleTime: DEFAULT_WAGMI_QUERY_STALE_TIME,
      gcTime: DEFAULT_WAGMI_QUERY_CACHE_TIME,
    },
  },
});

const projectId = process.env.NEXT_PUBLIC_CDP_PROJECT_ID ?? '';
if (!projectId) {
  const providerErrMessage =
    'To connect to all Wallets you need to provide a NEXT_PUBLIC_CDP_PROJECT_ID env variable';
  throw new Error(providerErrMessage);
}

export const wagmiConfig = createWagmiConfig();

function OnchainKitWrapperProvider({ children }: Props) {
  const chainId = useChainId();
  const chain = useMemo(() => getChainById(chainId.toString()), [chainId]);

  return (
    <OnchainKitProvider
      apiKey={process.env.NEXT_PUBLIC_ONCHAINKIT_API_KEY}
      chain={chain as NonNullable<typeof chain>}
      config={{
        appearance: {
          name: 'Verified Pools',
          logo: 'verifiedPools.svg',
          mode: 'auto',
          theme: 'base',
        },
        wallet: {
          display: 'modal',
          termsUrl: 'https://docs.verifiedpools.com/legal/terms-of-service',
          privacyUrl: 'https://docs.verifiedpools.com/legal/privacy-policy',
          supportedWallets: {
            rabby: true,
            frame: true,
          },
        },
      }}
      projectId={projectId}
    >
      {children}
    </OnchainKitProvider>
  );
}

/**
 * TODO Docs ~~~
 */
function OnchainProviders({ children }: Props) {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <OnchainKitWrapperProvider>{children}</OnchainKitWrapperProvider>
        {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </WagmiProvider>
  );
}

export default OnchainProviders;
